<template>
  <v-container class="pa-12">
    <v-card flat>
      <v-card-title class="text-h5 px-5 py-3">Site Settings</v-card-title>
      <v-card-text>
        <v-row class="px-5 py-3" dense>
          <v-col cols="12">
            <span class="font-weight-bold">Default Administrators</span>
          </v-col>
          <v-col cols="12">
            <v-autocomplete
              id="system-excursion-default--administrators"
              ref="selectDefaultAdministrator"
              v-model="defaultAdministrators"
              placeholder="Select one or more users"
              :items="clientAdminList"
              item-title="email"
              item-value="email"
              multiple
              clearable
              chips
              closable-chips
              hide-details
              single-line
              :hide-selected="hideSelected"
              variant="solo"
              density="compact"
              color="primary"
              :disabled="isLoadingUserList || isSaveInProgress"
              :loading="isLoadingUserList"
              @update:model-value="
                (event) => handleDefaultSelectionChange(event, $refs.selectDefaultAdministrator)
              "
            />
          </v-col>
          <v-col cols="12">
            <v-checkbox
              id="system-excursion-default--enforce-default-administrators"
              v-model="defaultAdministratorsEnforcementEnabled"
              label="Enforce default administrators"
              density="comfortable"
              color="primary"
              :disabled="isLoadingUserList || isSaveInProgress"
            ></v-checkbox>
          </v-col>
        </v-row>
        <v-row class="px-5" dense>
          <v-col cols="12">
            <span class="font-weight-bold">Default Pre-Approvers</span>
          </v-col>
          <v-col cols="12">
            <v-autocomplete
              id="system-excursion-default--approvers"
              ref="selectDefaultApprovers"
              v-model="defaultApprovers"
              placeholder="Select one or more users"
              :items="clientApproverList"
              item-title="email"
              item-value="email"
              multiple
              clearable
              chips
              closable-chips
              hide-details
              single-line
              :hide-selected="hideSelected"
              variant="solo"
              density="compact"
              color="primary"
              :disabled="isLoadingUserList || isSaveInProgress"
              :loading="isLoadingUserList"
              @update:model-value="
                (event) => handleDefaultSelectionChange(event, $refs.selectDefaultApprovers)
              "
            />
          </v-col>
          <v-col cols="12">
            <v-checkbox
              id="system-excursion-default--enforce-default-approvers"
              v-model="defaultApproversEnforcementEnabled"
              label="Enforce default pre-approvers"
              density="comfortable"
              color="primary"
              :disabled="isLoadingUserList || isSaveInProgress"
            ></v-checkbox>
          </v-col>
        </v-row>
        <v-row class="px-5" dense>
          <v-col cols="12">
            <span class="font-weight-bold">Default Final Approvers</span>
          </v-col>
          <v-col cols="12">
            <v-autocomplete
              id="system-excursion-default--final-approvers"
              ref="selectDefaultFinalApprovers"
              v-model="defaultFinalApprovers"
              placeholder="Select one or more users"
              :items="clientApproverList"
              item-title="email"
              item-value="email"
              multiple
              clearable
              chips
              closable-chips
              hide-details
              single-line
              :hide-selected="hideSelected"
              variant="solo"
              density="compact"
              color="primary"
              :disabled="isLoadingUserList || isSaveInProgress"
              :loading="isLoadingUserList"
              @update:model-value="
                (event) => handleDefaultSelectionChange(event, $refs.selectDefaultFinalApprovers)
              "
            />
          </v-col>
          <v-col cols="12">
            <v-checkbox
              id="system-excursion-default--enforce-default-final-approvers"
              v-model="defaultFinalApproversEnforcementEnabled"
              label="Enforce default final approvers"
              density="comfortable"
              color="primary"
              :disabled="isLoadingUserList || isSaveInProgress"
            ></v-checkbox>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <cs-button
          id="btn-save-client-admin-site-settings"
          primary
          :disabled="isLoadingUserList || isSaveInProgress"
          :loading="isSaveInProgress"
          label="Save"
          @click="save"
        ></cs-button>
      </v-card-actions>
    </v-card>
    <div>
      <cs-form-dialog
        id="dlg-client-admin-site-setting--generic"
        v-model="genericDialogDisplayed"
        body-id="dlg-client-admin-site-setting--generic-body"
        heading="Site Settings"
        :primary-action="{
          label: 'OK',
          eventName: 'primary-click'
        }"
        @primary-click="genericDialogDisplayed = false"
      >
        <template #cs-form-dialog-content>
          {{ genericDialogMessage }}
        </template>
      </cs-form-dialog>
    </div>
  </v-container>
</template>

<script>
import { uniqBy } from 'lodash';
import { mapState } from 'vuex';

export default {
  data() {
    return {
      genericDialogDisplayed: false,
      genericDialogMessage: '',
      isLoadingUserList: true,
      isSaveInProgress: false,
      defaultAdministrators: [],
      defaultApprovers: [],
      defaultFinalApprovers: [],
      defaultApproversEnforcementEnabled: false,
      defaultAdministratorsEnforcementEnabled: false,
      defaultFinalApproversEnforcementEnabled: false,
      clientAdminUsers: [],
      clientApproverUsers: [],
      hideSelected: false
    };
  },

  computed: {
    ...mapState({
      clientSiteSettingsButtonDiplayed: (state) => state.common.clientSiteSettingsButtonDiplayed,
      isAuthenticated: (state) => state.authorisation.isAuthenticated
    }),
    clientApproverList() {
      return uniqBy([...this.clientAdminUsers, ...this.clientApproverUsers], 'email');
    },
    clientAdminList() {
      return this.clientAdminUsers;
    }
  },

  async mounted() {
    const { axiosInstance, eventPluginCommonAdapter, stbApiAdapter } = this.$app;
    const isVendorAuthScheme =
      !!this.$route.query['vendor-sid'] && !!this.$route.query['oauth-signature'];
    if (!isVendorAuthScheme) {
      await this.$store.dispatch('authorisation/validateAndSetUserTokenOrLogout', {
        query: this.$route.query,
        axiosInstance
      });
    }
    await this.$store.dispatch('common/initClientConfig', {
      queryParam: isVendorAuthScheme ? this.$route.query : {},
      eventPluginCommonAdapter,
      stbApiAdapter
    });

    if (!this.clientSiteSettingsButtonDiplayed) {
      this.$router.push('/excursions');
    }

    if (this.isAuthenticated) {
      await this.init();
    }
  },

  methods: {
    async init() {
      this.isLoadingUserList = true;

      await this.initClientSiteData();
      await this.initUserList();

      this.isLoadingUserList = false;
      this.hideSelected = true;
    },
    async initClientSiteData() {
      const clientSiteSettingReponse = await this.$app.stbApiAdapter.getClientSiteSetting();

      if (!clientSiteSettingReponse.error && clientSiteSettingReponse.data) {
        const siteData = clientSiteSettingReponse.data;

        this.defaultAdministrators = siteData.defaultAdministrators || [];
        this.defaultApprovers = siteData.defaultApprovers || [];
        this.defaultFinalApprovers = siteData.defaultFinalApprovers || [];
        this.defaultAdministratorsEnforcementEnabled =
          siteData.defaultAdministratorsEnforcementEnabled || false;
        this.defaultApproversEnforcementEnabled =
          siteData.defaultApproversEnforcementEnabled || false;
        this.defaultFinalApproversEnforcementEnabled =
          siteData.defaultFinalApproversEnforcementEnabled || false;
      }
    },
    async initUserList() {
      const staffAdmins = [];
      const argHash = {
        filterParam: {
          role: 'client-admin',
          source: 'internal'
        }
      };

      const staffAdminResponse = await this.$app.stbApiAdapter.getClientStaffList(argHash);
      if (!staffAdminResponse?.error) {
        staffAdmins.push(...staffAdminResponse.data);
      }

      argHash.filterParam.role = 'client-admin-assistant';
      const staffAdminAssistantResponse = await this.$app.stbApiAdapter.getClientStaffList(argHash);
      if (!staffAdminAssistantResponse?.error) {
        staffAdmins.push(...staffAdminAssistantResponse.data);
      }

      this.clientAdminUsers.push(...uniqBy(staffAdmins, 'email'));

      argHash.filterParam.role = 'client-approver';
      const staffApproverResponse = await this.$app.stbApiAdapter.getClientStaffList(argHash);
      if (!staffApproverResponse?.error) {
        this.clientApproverUsers.push(...staffApproverResponse.data);
      }
    },
    async save() {
      this.isSaveInProgress = true;

      const newSiteData = {
        defaultAdministrators: [...this.defaultAdministrators],
        defaultApprovers: [...this.defaultApprovers],
        defaultFinalApprovers: [...this.defaultFinalApprovers],
        defaultAdministratorsEnforcementEnabled: this.defaultAdministratorsEnforcementEnabled,
        defaultApproversEnforcementEnabled: this.defaultApproversEnforcementEnabled,
        defaultFinalApproversEnforcementEnabled: this.defaultFinalApproversEnforcementEnabled
      };

      const patchResponse = await this.$app.stbApiAdapter.patchClientSiteSetting(newSiteData);

      if (!patchResponse?.error) {
        this.genericDialogMessage = 'Site settings has been saved.';
      } else {
        this.genericDialogMessage = 'Failed to save site settings.';
      }

      this.isSaveInProgress = false;
      this.genericDialogDisplayed = true;
    },
    handleDefaultSelectionChange(event, ref) {
      ref.blur();
      this.$nextTick(() => {
        ref.focus();
      });
    }
  }
};
</script>
